<template>
    <section
      ref="sectionRef"
      class="all-in-one-banner"
      :class="{
        'all-in-one-banner--visible': isVisible
      }"
    >
      <div class="all-in-one-banner__inner">
        <h2 class="all-in-one-banner__title">
          Всё <span>в одном месте</span>
        </h2>
  
        <div class="all-in-one-banner__visual">
          <div class="ambient ambient--left"></div>
          <div class="ambient ambient--right"></div>
  
          <img
            :src="currentImage"
            class="all-in-one-banner__image"
            alt="Все инструменты HR3 в одном месте"
            draggable="false"
          />

          <div class="all-in-one-analysis">
            <span class="all-in-one-analysis__label">
              Встроенные чаты
            </span>

            <h2 class="all-in-one-analysis__title">
              Общайтесь с кандидатами прямо в HR3
            </h2>

            <div class="all-in-one-analysis__content">
              <p>
                Больше не нужно переносить общение в Telegram,
                WhatsApp или другие мессенджеры. В HR3 есть
                встроенные чаты, поэтому вся коммуникация с
                кандидатами остаётся внутри сервиса.
              </p>

              <p>
                Вакансия, профиль кандидата, история отклика
                и переписка находятся рядом — без необходимости
                переключаться между несколькими приложениями.
              </p>

              <p>
                Можно сразу написать кандидату, уточнить детали,
                договориться о следующем этапе и продолжить
                общение там же, где вы работаете с откликом.
              </p>

              <p class="all-in-one-analysis__result">
                Вакансия, отклик и диалог —
                <strong>в одном месте.</strong>
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  
    <div class="separator"></div>
  </template>
  
  <script setup>
  import {
    computed,
    onMounted,
    onUnmounted,
    ref
  } from "vue";
  
  const lightImage = new URL(
    "../../assets/AllInOneBanner-light.png",
    import.meta.url
  ).href;
  
  const darkImage = new URL(
    "../../assets/AllInOneBanner-dark.png",
    import.meta.url
  ).href;
  
  const sectionRef = ref(null);
  const isVisible = ref(false);
  
  const theme = ref(
    document.documentElement.getAttribute(
      "data-theme"
    ) || "light"
  );
  
  const currentImage = computed(() => {
    return theme.value === "dark"
      ? darkImage
      : lightImage;
  });
  
  let themeObserver = null;
  let intersectionObserver = null;
  
  onMounted(() => {
    themeObserver = new MutationObserver(() => {
      theme.value =
        document.documentElement.getAttribute(
          "data-theme"
        ) || "light";
    });
  
    themeObserver.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: ["data-theme"]
      }
    );
  
    intersectionObserver =
      new IntersectionObserver(
        entries => {
          const entry = entries[0];
  
          if (!entry?.isIntersecting) {
            return;
          }
  
          isVisible.value = true;
          intersectionObserver?.disconnect();
        },
        {
          threshold: 0.12
        }
      );
  
    if (sectionRef.value) {
      intersectionObserver.observe(
        sectionRef.value
      );
    } else {
      requestAnimationFrame(() => {
        isVisible.value = true;
      });
    }
  });
  
  onUnmounted(() => {
    themeObserver?.disconnect();
    intersectionObserver?.disconnect();
  });
  </script>
  
  <style scoped>
  * {
    box-sizing: border-box;
  }
  
  .all-in-one-banner {
    position: relative;
  
    width: 100%;
  
    overflow: hidden;
  
    background: var(--background);
  
    opacity: 0;
    transform: translateY(18px);
  
    transition:
      opacity 0.75s ease,
      transform 0.75s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        );
  }
  
  .all-in-one-banner--visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  .all-in-one-banner__inner {
    width: 100%;
    max-width: 1440px;
  
    margin: 0 auto;
  
    padding:
      78px
      64px
      0px;
  }
  
  /* Title */
  
  .all-in-one-banner__title {
    position: relative;
    z-index: 5;
  
    margin:
      0
      0
      200px;
  
    color: var(--text);
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size:
      clamp(
        42px,
        4.3vw,
        64px
      );
  
    line-height: 1;
    letter-spacing: -0.045em;
  }
  
  .all-in-one-banner__title span {
    color: #2277e6;
  }
  
  /* Visual */
  
    .all-in-one-banner__visual {
    position: relative;

    width: 100%;
    min-height: 600px;

    display: flex;
    align-items: flex-end;
    justify-content: center;

    isolation: isolate;
  }
  
  .all-in-one-banner__image {
    position: relative;
    z-index: 2;
  
    width: min(
      900px,
      100%
    );
  
    height: auto;
  
    display: block;
  
    object-fit: contain;
  
    user-select: none;
  
    opacity: 0;
  
    transform:
      translateY(16px)
      scale(0.985);
  
    filter:
      drop-shadow(
        0
        28px
        54px
        rgba(
          32,
          67,
          120,
          0.07
        )
      );
  
    transition:
      opacity 0.9s ease 0.12s,
      transform 0.9s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        )
        0.12s;
  }
  
  .all-in-one-banner--visible
  .all-in-one-banner__image {
    opacity: 1;
  
    transform:
      translateY(0)
      scale(1);
  }
  
  /* Ambient */
  
  .ambient {
    position: absolute;
    z-index: 0;
  
    border-radius: 50%;
  
    pointer-events: none;
  
    filter: blur(115px);
  
    opacity: 0.11;
  
    animation:
      ambientFloat
      16s
      ease-in-out
      infinite;
  }
  
  .ambient--left {
    width: 360px;
    height: 360px;
  
    left: 12%;
    top: 14%;
  
    background: #4b8ff7;
  }
  
  .ambient--right {
    width: 300px;
    height: 300px;
  
    right: 13%;
    bottom: 10%;
  
    background: #7865ef;
  
    animation-delay: -7s;
  }
  
  @keyframes ambientFloat {
    0%,
    100% {
      transform:
        translate3d(
          0,
          0,
          0
        )
        scale(1);
    }
  
    50% {
      transform:
        translate3d(
          18px,
          -14px,
          0
        )
        scale(1.05);
    }
  }
  
  /* Separator */
  
  .separator {
    width: 100%;
    height: 1px;
  
    background:
      var(
        --separator_home,
        rgba(
          0,
          0,
          0,
          0.08
        )
      );
  }
  
  /* Dark */
  
  html[data-theme="dark"]
  .all-in-one-banner__image {
    filter:
      drop-shadow(
        0
        28px
        58px
        rgba(
          0,
          0,
          0,
          0.22
        )
      );
  }
  
  html[data-theme="dark"]
  .ambient {
    opacity: 0.16;
  }
  
  /* Laptop */
  
  @media (max-width: 1366px) {
    .all-in-one-banner__inner {
      padding:
        56px
        48px
        38px;
    }
  
    .all-in-one-banner__title {
      margin-bottom: 16px;
  
      font-size:
        clamp(
          38px,
          4vw,
          54px
        );
    }
  
    .all-in-one-banner__visual {
      min-height: 510px;
    }
  
    .all-in-one-banner__image {
      width: min(
        800px,
        100%
      );
    }
  }
  
  /* 13" laptop */
  
  @media (
    min-width: 769px
  ) and (
    max-height: 820px
  ) {
    .all-in-one-banner__inner {
      padding:
        40px
        46px
        26px;
    }
  
    .all-in-one-banner__title {
      margin-bottom: 10px;
  
      font-size:
        clamp(
          36px,
          3.7vw,
          48px
        );
    }
  
    .all-in-one-banner__visual {
      min-height: 430px;
    }
  
    .all-in-one-banner__image {
      width: min(
        720px,
        100%
      );
    }
  }
  
  /* Tablet */
  
  @media (max-width: 900px) {
    .all-in-one-banner__inner {
      padding:
        48px
        28px
        32px;
    }
  
    .all-in-one-banner__title {
      font-size:
        clamp(
          36px,
          7vw,
          50px
        );
    }
  
    .all-in-one-banner__visual {
      min-height: 0;
    }
  
    .all-in-one-banner__image {
      width: 92%;
    }
  }
  
  /* Mobile */
  
  @media (max-width: 768px) {
    .all-in-one-banner__inner {
      padding:
        38px
        18px
        26px;
    }
  
    .all-in-one-banner__title {
      margin-bottom: 22px;
  
      font-size: 35px;
      line-height: 1.03;
    }
  
    .all-in-one-banner__visual {
      margin:
        0
        -10px;
    }
  
    .all-in-one-banner__image {
      width: 108%;
  
      filter: none;
    }
  
    .ambient {
      display: none;
    }
  }
  
  @media (max-width: 480px) {
    .all-in-one-banner__inner {
      padding:
        32px
        16px
        22px;
    }
  
    .all-in-one-banner__title {
      font-size: 30px;
    }
  
    .all-in-one-banner__image {
      width: 116%;
    }
  }
  
  /* Reduced motion */
  
  @media (
    prefers-reduced-motion:
    reduce
  ) {
    .all-in-one-banner,
    .all-in-one-banner__image,
    .ambient {
      animation: none;
      transition: none;
    }
  
    .all-in-one-banner {
      opacity: 1;
      transform: none;
    }
  
    .all-in-one-banner__image {
      opacity: 1;
      transform: none;
    }
  }

/* All in one analysis */

.all-in-one-analysis {
  position: absolute;
  z-index: 5;

  top: 200px;
  right: 900px;

  width: 32%;
  max-width: 410px;

  padding: 22px 24px;

  transform: translateY(-44%);

  background:
    rgba(
      255,
      255,
      255,
      0.74
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.72
    );

  border-radius: 20px;

  box-shadow:
    0 18px 50px
    rgba(
      30,
      50,
      80,
      0.07
    );

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  opacity: 0;

  transition:
    opacity 0.8s ease 0.3s,
    transform 0.8s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      0.3s;
}

.all-in-one-banner--visible
.all-in-one-analysis {
  opacity: 1;

  transform: translateY(-50%);
}

/* Label */

.all-in-one-analysis__label {
  display: inline-flex;
  align-items: center;

  margin-bottom: 11px;
  padding: 5px 8px;

  color: #2277e6;

  background:
    rgba(
      34,
      119,
      230,
      0.08
    );

  border:
    1px solid
    rgba(
      34,
      119,
      230,
      0.1
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
  line-height: 1;

  letter-spacing: 0.07em;
  text-transform: uppercase;
}

/* Title */

.all-in-one-analysis__title {
  margin:
    0
    0
    13px;

  color: #20242c;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 20px;
  line-height: 1.15;
  letter-spacing: -0.025em;
}

/* Content */

.all-in-one-analysis__content {
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.all-in-one-analysis__content p {
  margin: 0;

  color: #626a77;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;
  line-height: 1.48;
}

.all-in-one-analysis__content strong {
  color: #353b45;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-weight: normal;
}

/* Result */

.all-in-one-analysis__result {
  padding:
    11px
    12px !important;

  color:
    #395a88 !important;

  background:
    linear-gradient(
      135deg,
      rgba(
        34,
        119,
        230,
        0.07
      ),
      rgba(
        83,
        104,
        232,
        0.045
      )
    );

  border:
    1px solid
    rgba(
      34,
      119,
      230,
      0.08
    );

  border-radius: 11px;
}

.all-in-one-analysis__result strong {
  color: #2277e6;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-weight: normal;
}

/* Dark */

html[data-theme="dark"]
.all-in-one-analysis {
  background:
    rgba(
      28,
      28,
      30,
      0.78
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0 20px 55px
    rgba(
      0,
      0,
      0,
      0.22
    );
}

html[data-theme="dark"]
.all-in-one-analysis__label {
  color: #72a8ff;

  background:
    rgba(
      63,
      137,
      241,
      0.12
    );

  border-color:
    rgba(
      74,
      145,
      246,
      0.16
    );
}

html[data-theme="dark"]
.all-in-one-analysis__title {
  color: #f2f3f5;
}

html[data-theme="dark"]
.all-in-one-analysis__content p {
  color: #a7adb7;
}

html[data-theme="dark"]
.all-in-one-analysis__content strong {
  color: #e1e4e9;
}

html[data-theme="dark"]
.all-in-one-analysis__result {
  color:
    #9fc3ff !important;

  background:
    linear-gradient(
      135deg,
      rgba(
        40,
        135,
        247,
        0.12
      ),
      rgba(
        83,
        104,
        232,
        0.07
      )
    );

  border-color:
    rgba(
      74,
      145,
      246,
      0.14
    );
}

html[data-theme="dark"]
.all-in-one-analysis__result strong {
  color: #72a8ff;
}

/* Laptop */

@media (max-width: 1366px) {
  .all-in-one-analysis {
    right: 0;

    width: 33%;
    max-width: 355px;

    padding: 17px 18px;

    border-radius: 16px;
  }

  .all-in-one-analysis__label {
    margin-bottom: 8px;

    font-size: 8px;
  }

  .all-in-one-analysis__title {
    margin-bottom: 10px;

    font-size: 17px;
  }

  .all-in-one-analysis__content {
    gap: 8px;
  }

  .all-in-one-analysis__content p {
    font-size: 9.5px;
    line-height: 1.42;
  }

  .all-in-one-analysis__result {
    padding:
      8px
      9px !important;
  }
}

/* 13" laptop */

@media (
  min-width: 769px
) and (
  max-height: 820px
) {
  .all-in-one-analysis {
    width: 32%;
    max-width: 325px;

    padding: 14px 16px;
  }

  .all-in-one-analysis__title {
    margin-bottom: 8px;

    font-size: 15px;
  }

  .all-in-one-analysis__content {
    gap: 6px;
  }

  .all-in-one-analysis__content p {
    font-size: 8.5px;
    line-height: 1.38;
  }

  .all-in-one-analysis__result {
    padding:
      7px
      8px !important;
  }
}

/* Tablet / mobile */

@media (max-width: 900px) {
  .all-in-one-banner__visual {
    flex-direction: column;
  }

  .all-in-one-analysis {
    position: relative;

    top: auto;
    right: auto;

    width: 100%;
    max-width: 680px;

    margin-top: 16px;
    padding: 18px 20px;

    transform: translateY(10px);
  }

  .all-in-one-banner--visible
  .all-in-one-analysis {
    transform: translateY(0);
  }

  .all-in-one-analysis__title {
    font-size: 18px;
  }

  .all-in-one-analysis__content p {
    font-size: 11px;
  }
}

  </style>